import { useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from './api-client';
import { API_BASE } from './api-base';
import { queryKeys } from './query-client';

/**
 * The activity log — what agents did on the user's behalf, newest first, over a rolling window.
 *
 * The web twin of the native `ActivityLogScreen` (iOS `Features/Agents/ActivityLogScreen.swift`,
 * Android `loadActivityLog`). The grouping below is a deliberate 1:1 port of that screen's
 * `ActivityLogGrouping`: same day buckets, same collapse rule, same drop-on-unparseable-date, so the
 * three surfaces section the same rows the same way. Keep them in step.
 */

/** One row. Mirrors `WebAgentAction` in `pages/api/agent-actions.ts`. */
export type ActivityAction = {
  id: string;
  createdDate?: string;
  agentId?: string;
  label?: string;
  action?: string;
  reasoning?: string;
  relatedTaskId?: string;
};

export type ActivityDayBucket = 'today' | 'yesterday' | 'older';

export type ActivityDayGroup = {
  /** Stable key: one bucket for today, one for yesterday, one per distinct older day. */
  id: string;
  bucket: ActivityDayBucket;
  /** A date inside the group — what an `older` section heading is formatted from. */
  refDate: Date;
  rows: ActivityAction[];
};

/** The window the log covers, in days. Matches the native screen's 30. */
export const ACTIVITY_WINDOW_DAYS = 30;

/** Page size. Matches the native store's 30-per-page infinite scroll. */
const PAGE_SIZE = 30;

/** Parse an ISO `createdDate`; null when absent or unparseable (such a row cannot be placed in a day). */
export function parseActionDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : new Date(ms);
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Group actions by local calendar day relative to `now`, preserving encounter order (the API returns
 * newest-first). Today and Yesterday each collapse into ONE group; every distinct older day gets its
 * own. Rows with no usable `createdDate` are dropped rather than piled into an arbitrary day.
 *
 * Pure — `now` is injected — so it is testable without depending on the machine clock or timezone.
 */
export function groupActivity(actions: ActivityAction[], now: Date): ActivityDayGroup[] {
  const today = startOfDay(now);
  const oneDay = 24 * 60 * 60 * 1000;
  const order: string[] = [];
  const rowsByKey = new Map<string, ActivityAction[]>();
  const metaByKey = new Map<string, { bucket: ActivityDayBucket; refDate: Date }>();

  for (const action of actions) {
    const date = parseActionDate(action.createdDate);
    if (!date) continue;
    const day = startOfDay(date);
    // Whole-day difference. Derived from the two local midnights, so DST shifts cannot round it off.
    const delta = Math.round((today - day) / oneDay);
    const bucket: ActivityDayBucket = delta === 0 ? 'today' : delta === 1 ? 'yesterday' : 'older';
    const key = bucket === 'today' ? 'T' : bucket === 'yesterday' ? 'Y' : `O:${day}`;
    if (!rowsByKey.has(key)) {
      order.push(key);
      rowsByKey.set(key, []);
      metaByKey.set(key, { bucket, refDate: date });
    }
    rowsByKey.get(key)!.push(action);
  }

  return order.map((key) => {
    const meta = metaByKey.get(key)!;
    return { id: key, bucket: meta.bucket, refDate: meta.refDate, rows: rowsByKey.get(key) ?? [] };
  });
}

type ActivityPage = { actions: ActivityAction[]; cursor: string | null };

/**
 * Flatten the fetched pages, dropping a row whose id was already seen. A cursor page can repeat a row
 * when rows are written mid-scroll, and React Query v4 refetches EVERY page on an invalidation — either
 * way a duplicate id collides as a React key. Same guard `use-notifications-feed` applies to the same
 * shape of feed. First occurrence wins, so the newest copy is the one kept.
 */
export function dedupeActions(pages: { actions: ActivityAction[] }[]): ActivityAction[] {
  const seen = new Set<string>();
  const out: ActivityAction[] = [];
  for (const page of pages) {
    for (const a of page.actions) {
      if (a.id) {
        if (seen.has(a.id)) continue;
        seen.add(a.id);
      }
      out.push(a);
    }
  }
  return out;
}

async function fetchActivityPage(cursor: string | null): Promise<ActivityPage> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor) {
    // Page 2+: cursor only. It already encodes the window, and the service rejects filter+cursor.
    params.set('cursor', cursor);
  } else {
    params.set('since', new Date(Date.now() - ACTIVITY_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString());
  }
  // API_BASE is the mount prefix — without it the call 404s wherever the app is not at the root.
  const res = await apiFetch(`${API_BASE}/api/agent-actions?${params.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      message = body.message || body.error || message;
    } catch {
      // Non-JSON error body — keep the status-derived message.
    }
    throw new Error(message);
  }
  const body = (await res.json()) as ActivityPage;
  return { actions: body.actions ?? [], cursor: body.cursor ?? null };
}

export type ActivityState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; actions: ActivityAction[] };

/**
 * The log, paged. A failed FIRST load surfaces as an error — never as an empty log, because "we could
 * not read it" and "nothing happened" are different answers and only one of them is honest. A failed
 * later page keeps what is already on screen.
 */
export function useActivity(): {
  state: ActivityState;
  hasMore: boolean;
  loadingMore: boolean;
  /** Pulls the next page. Returns false when there was nothing to pull, which the caller treats as
   *  "stop asking" — see the sentinel in `ActivityBody`. */
  loadMore: () => boolean;
} {
  const query = useInfiniteQuery({
    queryKey: queryKeys.activity,
    queryFn: ({ pageParam }: { pageParam?: string | null }) => fetchActivityPage(pageParam ?? null),
    getNextPageParam: (last: ActivityPage) => last.cursor || undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  // Stable across renders — an effect that re-arms an IntersectionObserver depends on this identity,
  // and a fresh closure per render tears the observer down and rebuilds it on every paint.
  const loadMore = useCallback((): boolean => {
    if (!hasNextPage || isFetchingNextPage) return false;
    void fetchNextPage();
    return true;
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const hasMore = !!hasNextPage;
  const loadingMore = isFetchingNextPage;

  if (query.data) {
    return { state: { kind: 'ready', actions: dedupeActions(query.data.pages) }, hasMore, loadingMore, loadMore };
  }
  if (query.status === 'error') {
    const message = query.error instanceof Error ? query.error.message : String(query.error);
    return { state: { kind: 'error', message }, hasMore: false, loadingMore: false, loadMore };
  }
  return { state: { kind: 'loading' }, hasMore: false, loadingMore: false, loadMore };
}
